import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController, MenuController, NavController } from '@ionic/angular';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src';
import { ChooseLocationFacade } from '@symphony-solo/domain/choose-location/src';
import { CompanyConfigFacade, CompanyConfigType } from '@symphony-solo/domain/company-config/src';
import { AppConfigService, CompanyGroupFacade } from '@symphony-solo/domain/company-group/src';
import { AlertService } from '@symphony-solo/shared/alert/src';
import { Tile } from '@symphony-solo/shared/components/src/lib/models/tile.model';
import {
  ABOUT,
  FAQ,
  LOGIN,
  PRIVACY_POLICY,
  SIGNUP,
  TERMS_AND_CONDITIONS,
  ACCOUNT_DETAILS_TABS,
  MY_CARDS_TABS,
  CLICKCOLLECT_PURCHASE_HISTORY_TABS,
  PURCHASE_HISTORY_TABS,
  ACCOUNT_SETTINGS_TABS,
  ACCESSIBILITY_STATEMENT,
  ALLERGEN_STATEMENT,
  APP_SETTINGS_TABS,
} from '@symphony-solo/shared/utils/src';
import { Icons } from '@symphony-solo/shared/common-ui/src/';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { PlatformIdentityService } from '@symphony-solo/shared/platform-identity/src';
import { map } from 'rxjs/operators';
@Component({
  selector: 'symphony-solo-side-menu-page',
  templateUrl: './side-menu-page.component.html',
  styleUrls: ['./side-menu-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuPageComponent implements OnInit, OnDestroy {
  public viewModel$: Observable<any> = combineLatest([
    this.authenticationFacade.isAuthenticated$,
    this.authenticationFacade.accountDetails$,
    this.authenticationFacade.userName$,
    this.companyGroupFacade.getCompanyGroup$,
    this.companyConfig.companyConfigIndex$,
    this.companyConfig.anonymousBrowsing$,
  ]).pipe(
    map(([isAuthenticated, accountDetails, userName, companyGroup, companyConfigIndex, anonymousBrowsing]) => ({
      isAuthenticated,
      accountDetails,
      userName,
      companyGroup: this.setGlobalStyle(companyGroup, companyConfigIndex),
      companyConfigIndex,
      isGuest: anonymousBrowsing,
      menuList: this.processSideMenu(companyConfigIndex, anonymousBrowsing, isAuthenticated, accountDetails),
    }))
  );

  public loginBtnText = '';
  public logoutBtnText = '';
  public isNative = false;
  public isMobileWeb = false;

  @Input() public isMenuReset: boolean;

  public isAccountAvailable = true;
  public companyLogo: string;
  public iconsSvg = Icons.svg;

  public selectFunctionTiles: Tile[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    public modalCtrl: ModalController,
    public authenticationFacade: AuthenticationFacade,
    private chooseLocationFacade: ChooseLocationFacade,
    private navController: NavController,
    private menuCtrl: MenuController,
    private companyConfig: CompanyConfigFacade,
    private alertService: AlertService,
    private companyGroupFacade: CompanyGroupFacade,
    private platformIdentityService: PlatformIdentityService,
    private appConfigService: AppConfigService,
    private elem: ElementRef
  ) {}

  public ngOnInit(): void {
    this.companyGroupFacade.getCompanyGroupLocal();
    this.menuCtrl.enable(false);
    this.menuCtrl.swipeGesture(false);
    this.isNative = this.platformIdentityService.isNative();
    this.isMobileWeb = this.platformIdentityService.isMobileWeb();

    setTimeout(() => {
      this.subscriptions.push(
        this.authenticationFacade.getAuthenticationToken().subscribe((res) => {
          if (!!res) {
            setTimeout(() => {
              this.menuCtrl.enable(true);
            }, 1000);
          } else {
            this.menuCtrl.enable(false);
          }
        }),
        this.companyGroupFacade.getCompanyGroup$.subscribe((companyGroup) => {
          this.appConfigService.setConfig(companyGroup);
        })
      );
    }, 0);
  }

  public addGlobalFont(fontUrl: string) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('type', 'text/css');
    link.setAttribute('href', fontUrl);
    document.head.appendChild(link);
  }

  public addGlobalStyle(css: string) {
    const styleElement = document.createElement('style');
    styleElement.appendChild(document.createTextNode(css));
    document.head.appendChild(styleElement);
  }

  public goBack(): void {
    this.navController.pop();
  }

  public createAccount(): void {
    this.navController.navigateForward(SIGNUP);
  }

  public login(): void {
    this.chooseLocationFacade.clearState('chooseLocation');
    this.navController.navigateForward(LOGIN);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public navigateToMenuItem({ path, title, condition, isNavigateRoot, id }) {
    if (path === 'sign-out') {
      this.logout();
      return;
    }

    if (path !== '') {
      if (condition) {
        this.loginSignupAlert(title);
      } else {
        if (isNavigateRoot) {
          this.navController.navigateRoot(path);
        } else {
          this.navController.navigateForward(path);
        }
        this.menuCtrl.close();
      }
    } else {
      this.toggleSubMenu(id);
    }
  }

  private toggleSubMenu(id) {
    const parentMenuItem = this.elem.nativeElement.querySelector('#parent-' + id);
    const children = this.elem.nativeElement.querySelector('#children-' + id);
    const allMenuItems = this.elem.nativeElement.querySelectorAll('.menu-item');

    const addIcon = this.elem.nativeElement.querySelector('#action-add-' + id);
    const removeIcon = this.elem.nativeElement.querySelector('#action-remove-' + id);

    for (const item of allMenuItems) {
      const itemParent = item.querySelector('.parent');
      const itemChildren = item.querySelector('.children');

      if (itemParent && itemParent.classList.contains('has-children') && parentMenuItem !== itemParent) {
        itemParent.classList.remove('has-children');
        if (itemChildren !== null) {
          itemChildren.classList.add('ion-hide');
          const itemAddIcon = item.querySelector('.action-add');
          const itemRemoveIcon = item.querySelector('.action-remove');
          itemAddIcon.classList.remove('ion-hide');
          itemRemoveIcon.classList.add('ion-hide');
        }
      }
    }

    parentMenuItem.classList.toggle('has-children');
    children.classList.toggle('ion-hide');
    addIcon.classList.toggle('ion-hide');
    removeIcon.classList.toggle('ion-hide');
  }

  private processSideMenu(companyConfigIndex, anonymousBrowsing, isAuthenticated, accountDetails) {
    if (!companyConfigIndex) {
      return;
    }

    this.loginBtnText = companyConfigIndex[CompanyConfigType.TextBasicLogin];
    this.logoutBtnText = companyConfigIndex[CompanyConfigType.TextSignOut];

    const accountSubMenu = [
      {
        id: CompanyConfigType.TextAccountDetails,
        title: companyConfigIndex[CompanyConfigType.TextAccountDetails],
        path: ACCOUNT_DETAILS_TABS,
        iconSrc: null,
        children: null,
        condition: anonymousBrowsing && !isAuthenticated,
        isNavigateRoot: true,
        hide: false,
      },
      {
        id: 0,
        title: companyConfigIndex[CompanyConfigType.SoloAccountSettingsText],
        path: ACCOUNT_SETTINGS_TABS,
        iconSrc: null,
        children: null,
        condition: anonymousBrowsing && !isAuthenticated,
        isNavigateRoot: true,
        hide: false,
      },
      {
        id: 0,
        title: companyConfigIndex[CompanyConfigType.SoloMyCardsText],
        path: MY_CARDS_TABS,
        iconSrc: null,
        children: null,
        condition: anonymousBrowsing && !isAuthenticated,
        isNavigateRoot: false,
        hide: !isAuthenticated,
      },
    ];

    const helpCenterSubMenu = [
      {
        id: CompanyConfigType.TextTermsandConditions,
        title: companyConfigIndex[CompanyConfigType.TextTermsandConditions],
        path: TERMS_AND_CONDITIONS,
        iconSrc: null,
        children: null,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
      {
        id: CompanyConfigType.TextPrivacyPolicy,
        title: companyConfigIndex[CompanyConfigType.TextPrivacyPolicy],
        path: PRIVACY_POLICY,
        iconSrc: null,
        children: null,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
      {
        id: CompanyConfigType.TextFAQ,
        title: companyConfigIndex[CompanyConfigType.TextFAQ],
        path: FAQ,
        iconSrc: null,
        children: null,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
      {
        id: 0,
        title: companyConfigIndex[CompanyConfigType.SoloAccessibilityStatementText],
        path: ACCESSIBILITY_STATEMENT,
        iconSrc: null,
        children: null,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
      {
        id: 0,
        title: companyConfigIndex[CompanyConfigType.SoloAllergenStatementText],
        path: ALLERGEN_STATEMENT,
        iconSrc: null,
        children: null,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
    ];

    const settingsSubMenu = [
      {
        id: 'settings-11',
        title: 'App Settings',
        path: APP_SETTINGS_TABS,
        iconSrc: null,
        children: null,
        condition: anonymousBrowsing && !isAuthenticated,
        isNavigateRoot: false,
        hide: false,
      },
    ];

    const menuList = [
      {
        id: CompanyConfigType.TextYourAccount,
        title: companyConfigIndex[CompanyConfigType.TextYourAccount],
        path: '',
        iconSrc: this.iconsSvg.user,
        children: accountSubMenu,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
      {
        id: CompanyConfigType.SoloScanandGoHistoryText,
        title: companyConfigIndex[CompanyConfigType.SoloScanandGoHistoryText],
        path: PURCHASE_HISTORY_TABS,
        iconSrc: this.iconsSvg.orderHistoryFilled,
        children: null,
        condition: anonymousBrowsing && !isAuthenticated,
        isNavigateRoot: false,
        hide: this.hideScanandGoHistory(isAuthenticated, accountDetails, companyConfigIndex),
      },
      {
        id: CompanyConfigType.SoloClickandCollectHistoryText,
        title: companyConfigIndex[CompanyConfigType.SoloClickandCollectHistoryText],
        path: CLICKCOLLECT_PURCHASE_HISTORY_TABS,
        iconSrc: this.iconsSvg.timeMachineFilled,
        children: null,
        condition: anonymousBrowsing && !isAuthenticated,
        isNavigateRoot: false,
        hide: this.hideClickAndCollectHistory(isAuthenticated, accountDetails, companyConfigIndex),
      },
      {
        id: CompanyConfigType.TextHelpCentreTitle,
        title: companyConfigIndex[CompanyConfigType.TextHelpCentreTitle],
        path: '',
        iconSrc: this.iconsSvg.helpFilled,
        children: helpCenterSubMenu,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
      {
        id: 'settings-1',
        title: 'Settings',
        path: '',
        iconSrc: this.iconsSvg.settings,
        children: settingsSubMenu,
        condition: false,
        isNavigateRoot: false,
        hide: !this.isNative,
      },
      {
        id: CompanyConfigType.TextAbout,
        title: companyConfigIndex[CompanyConfigType.TextAbout],
        path: ABOUT,
        iconSrc: this.iconsSvg.aboutFilled,
        children: null,
        condition: false,
        isNavigateRoot: false,
        hide: false,
      },
    ];

    return menuList;
  }

  private loginSignupAlert(title: string) {
    const signIn = {
      text: 'Sign In',
      cssClass: 'okButton centerButton',
      handler: () => {
        this.navController.navigateRoot(LOGIN);
      },
    };
    const createAccount = {
      text: 'Create Account',
      cssClass: 'okButton centerButton',
      handler: () => {
        this.navController.navigateRoot(SIGNUP);
      },
    };
    this.alertService.presentAlert('Attention', `Please sign in or create an account to access ${title}`, [signIn, createAccount]);
  }

  private setGlobalStyle(companyGroup, companyConfigIndex) {
    if (companyGroup) {
      this.companyLogo = companyGroup.logoImageURL;

      let style = `
        .bg-image {
          background-image: url(${companyGroup.backgroundImageURL}) !important;
        }
        .background-image {
          background-image: url(${companyGroup.backgroundImageURL}) !important;
        }
        
      `;

      if (
        companyConfigIndex &&
        companyConfigIndex[CompanyConfigType.SoloFontFamily] &&
        companyConfigIndex[CompanyConfigType.SoloFontFamilyUrl]
      ) {
        this.addGlobalFont(companyConfigIndex[CompanyConfigType.SoloFontFamilyUrl]);
        style += `* {
          font-family: ${companyConfigIndex[CompanyConfigType.SoloFontFamily]};
        }
        ion-label{
          font-family: ${companyConfigIndex[CompanyConfigType.SoloFontFamily]} !important;
        }`;
      }

      if (companyConfigIndex && companyConfigIndex[CompanyConfigType.SoloAltColourForTextIconOnBackground]) {
        style += `.text-contrast-color {
          color: ${companyConfigIndex[CompanyConfigType.SoloAltColourForTextIconOnBackground]};
        }
        :root {
          --contrast-color-onbackground: ${companyConfigIndex[CompanyConfigType.SoloAltColourForTextIconOnBackground]};
        }
        .icon-contrast-color {
          color: ${companyConfigIndex[CompanyConfigType.SoloAltColourForTextIconOnBackground]};
        }`;
      }

      if (companyConfigIndex && companyConfigIndex[CompanyConfigType.SoloClickCollectLocationTemplateType] === '1') {
        style += `.template-border-r-8 {
          border-radius: 10px;
          overflow: hidden;
        }
        .btn-border-r-8 {
          --border-radius: 10px;
        }
        .border-no {
          box-shadow: unset !important;
        }`;
      }

      this.addGlobalStyle(style);
    }

    return companyGroup;
  }

  private logout(): void {
    this.chooseLocationFacade.clearState('chooseLocation');
    this.authenticationFacade.logout();
  }

  private hideScanandGoHistory(isAuthenticated, accountDetails, companyConfigIndex): boolean {
    if (accountDetails) {
      if (!isAuthenticated) {
        return !companyConfigIndex[CompanyConfigType.SoloFeatureHideScanAndGoHistory];
      }

      if (isAuthenticated) {
        if (!companyConfigIndex[CompanyConfigType.SoloFeatureHideScanAndGoHistory]) {
          if (accountDetails.scanAndGoAllowed) {
            return false;
          }
        }
      }
    }

    return true;
  }

  private hideClickAndCollectHistory(isAuthenticated, accountDetails, companyConfigIndex): boolean {
    if (accountDetails) {
      if (!isAuthenticated) {
        return !companyConfigIndex[CompanyConfigType.SoloFeatureHideClickandCollectHistory];
      }

      if (isAuthenticated) {
        if (!companyConfigIndex[CompanyConfigType.SoloFeatureHideClickandCollectHistory]) {
          return false;
        }
      }
    }
    return true;
  }
}
